import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import MarkdownRenderer from '../components/blog/MarkdownRenderer';
import { getProjectBySlug, getAllProjects } from '../lib/markdown';
import type { Project } from '../types/project';

export default function ProjectDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [relatedProjects, setRelatedProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!slug) return;

    getProjectBySlug(slug).then(async (foundProject) => {
      if (!foundProject) {
        setLoading(false);
        return;
      }

      setProject(foundProject);

      // Get related projects with shared tech stack
      const allProjects = await getAllProjects();
      const related = allProjects
        .filter(p => p.slug !== slug && p.techStack.some((t: string) => foundProject.techStack.includes(t)))
        .slice(0, 3);
      setRelatedProjects(related);

      setLoading(false);
    });
  }, [slug]);

  if (loading) {
    return (
      <div className="container-custom py-20">
        <div className="text-center text-xl">加载中...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container-custom py-20 text-center">
        <h1 className="text-4xl font-heading font-bold mb-4">项目未找到</h1>
        <p className="text-xl text-gray-600 mb-8">
          抱歉，您访问的项目不存在。
        </p>
        <Link
          to="/projects"
          className="btn-neubrutal bg-cta text-white px-6 py-3"
        >
          返回项目列表
        </Link>
      </div>
    );
  }

  return (
    <div className="container-custom py-12">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="mb-8 text-cta font-medium hover:underline flex items-center gap-2"
      >
        ← 返回
      </button>

      {/* Header */}
      <article>
        <header className="mb-8">
          <h1 className="text-5xl md:text-6xl font-heading font-bold mb-4">
            {project.title}
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            {project.description}
          </p>

          {/* Tech stack */}
          <div className="flex flex-wrap gap-3 mb-6">
            {project.techStack.map((tech) => (
              <span
                key={tech}
                className="px-4 py-2 border-2 border-black bg-secondary rounded-full font-medium"
              >
                {tech}
              </span>
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-4">
            {project.liveUrl && (
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-neubrutal bg-cta text-white px-6 py-3"
              >
                查看演示 →
              </a>
            )}
            {project.githubUrl && (
              <a
                href={project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-neubrutal bg-white text-black px-6 py-3"
              >
                GitHub 仓库
              </a>
            )}
          </div>
        </header>

        {/* Cover image */}
        {project.coverImage && (
          <div className="aspect-video mb-8 bg-gradient-to-br from-primary/20 to-cta/20 border-3 border-black shadow-neubrutal rounded-lg flex items-center justify-center">
            <span className="text-9xl">🚀</span>
          </div>
        )}

        {/* Content */}
        <div className="bg-white border-3 border-black shadow-neubrutal rounded-lg p-8 md:p-12">
          <MarkdownRenderer content={project.content} />
        </div>
      </article>

      {/* Related projects */}
      {relatedProjects.length > 0 && (
        <section className="mt-16">
          <h2 className="text-3xl font-heading font-bold mb-6">
            相关项目
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {relatedProjects.map((related) => (
              <Link
                key={related.slug}
                to={`/projects/${related.slug}`}
                className="card-neubrutal bg-white p-6 rounded-lg"
              >
                <h3 className="text-xl font-heading font-bold mb-2">
                  {related.title}
                </h3>
                <p className="text-gray-600 mb-3 line-clamp-2">
                  {related.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {related.techStack.slice(0, 2).map((tech) => (
                    <span
                      key={tech}
                      className="text-xs px-2 py-1 border-2 border-black bg-secondary rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
